import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/library/dialog";
import { useEligibleTrackUsers } from "@/features/stage/api/useEligibleTrackUsers";
import type { TrackAssignee } from "@/features/stage/types/stageTypes";
import { cn } from "@/lib/utils";

interface TrackUserPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string | number | null | undefined;
  trackId: number | null;
  trackName?: string;
  /** Who handles it right now — preselected. */
  current?: TrackAssignee[];
  /** "add" = the track is being put on the lead; "change" = hand it over. */
  mode: "add" | "change";
  isSaving?: boolean;
  /** `null` = add the track with nobody on it (no eligible member). */
  onConfirm: (userId: number | "auto" | null) => void;
}

/**
 * "Who will handle this track?" — opens when a track some role holds at "Own"
 * is added to a lead, and from the name chip to hand it over. One person per
 * role: the list is grouped by role and picking inside a role replaces that
 * role's current holder (the server enforces the same rule).
 */
export default function TrackUserPickerDialog({
  open,
  onOpenChange,
  leadId,
  trackId,
  trackName,
  current = [],
  mode,
  isSaving,
  onConfirm,
}: TrackUserPickerDialogProps) {
  const { data: users = [], isLoading } = useEligibleTrackUsers(
    leadId,
    trackId,
    open
  );
  const [picked, setPicked] = useState<number | "auto">("auto");

  useEffect(() => {
    if (!open) return;
    const held = current.find((c) => users.some((u) => u.id === c.id));
    setPicked(held ? held.id : "auto");
    // Re-seed only when the dialog opens or the list arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, users.length]);

  const byRole = useMemo(() => {
    const map = new Map<string, typeof users>();
    for (const u of users) {
      const key = u.roleName || "Members";
      map.set(key, [...(map.get(key) ?? []), u]);
    }
    return [...map.entries()];
  }, [users]);

  const row = (active: boolean) =>
    cn(
      "w-full flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors",
      active
        ? "border-[var(--color-success)] bg-[#edfaf5]"
        : "border-[var(--color-black-1)] bg-white hover:bg-[#f7f9f8]"
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[440px] p-0 gap-0 overflow-hidden rounded-[24px]"
        showCloseButton={true}
      >
        <DialogHeader className="p-[16px] pb-[11px] pt-[15px] border-b border-[#E6E8E7] text-left">
          <DialogTitle className="!text-[18px] leading-normal font-medium text-[#051912] tracking-[-0.72px]">
            Who will handle {trackName ?? "this track"}?
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[#505E59] tracking-[-0.26px]">
            {mode === "add"
              ? "Only this member will see the lead in their own track list."
              : "The track and its follow-up move to the new member from today."}
          </DialogDescription>
        </DialogHeader>

        <div className="p-[16px] max-h-[52vh] overflow-y-auto flex flex-col gap-3">
          {isLoading ? (
            <p className="text-sm text-[#505e59] text-center py-4">
              Loading members…
            </p>
          ) : users.length === 0 ? (
            <p className="text-sm text-[#505e59] text-center py-4">
              No member with Own on this track belongs to this lead's branch.
            </p>
          ) : (
            <>
              <button
                type="button"
                className={row(picked === "auto")}
                onClick={() => setPicked("auto")}
              >
                <span className="text-sm font-medium text-[#051912]">
                  Auto-assign
                </span>
                <span className="text-[12px] text-[#697571]">
                  next member in turn
                </span>
              </button>

              {byRole.map(([roleName, list]) => (
                <div key={roleName} className="flex flex-col gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#697571]">
                    {roleName}
                  </p>
                  {list.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      className={row(picked === u.id)}
                      onClick={() => setPicked(u.id)}
                    >
                      <span className="text-sm font-medium text-[#051912] truncate">
                        {u.name}
                      </span>
                      <span className="text-[12px] text-[#697571] shrink-0">
                        {u.load} active
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>

        <DialogFooter className="p-[16px] pt-0 gap-2 sm:justify-end">
          <Button variant="cancel" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              isSaving || isLoading || (users.length === 0 && mode !== "add")
            }
            onClick={() => onConfirm(users.length === 0 ? null : picked)}
          >
            {mode === "add" ? "Add track" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
